// SPEC manna-spec.md §1.3.1, §2.2.6, BUG-018 — UI-side lazy MFA step-up.
//
// Reusable dialog that elevates the current session to MFA-active without
// minting an agent fresh-MFA token. Used by RequireInternalMfa to recover
// when an Entra session was issued without MFA evidence (e.g. trusted-device
// Conditional Access skip): the user's JWT carries internal roles but the
// `is_mfa_active` claim is false, so platform.has_role() short-circuits and
// every RLS policy denies. This dialog gives the user an explicit "verify"
// affordance instead of a silently-broken UI.
//
// Single-path TOTP step-up (BUG-018): on Entra Free, the upstream MFA
// evidence (amr/acr) is not surfaced into Supabase claims.amr or
// auth.identities.identity_data, so platform.custom_access_token_hook can
// only flip is_mfa_active=true via path-3 (verified TOTP factor in
// auth.mfa_factors). Therefore: every internal-role user — Entra SSO or
// not — verifies with their Manna-side TOTP factor here. The dialog's
// auto-challenge runs the same way for both providers; if no factor is
// enrolled yet, deep-link to /security.
//
// Differs from FreshMfaDialog in that this dialog does NOT mint a server-
// side fresh-MFA token row — that token is only used by the agentic
// platform for sensitive/critical actions (agentic-prd §6.7). Lazy step-up
// only needs the JWT claim flipped.

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { Link as RouterLink } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuthStore } from "../stores/auth";
import { formatError } from "../lib/errors";
import { paths } from "../routes/paths";

interface Props {
  open: boolean;
  title?: string;
  description?: string;
  /** Cancel CTA copy. Default: "Cancel". */
  cancelLabel?: string;
  onSuccess: () => void;
  onCancel: () => void;
  /** Called when the user clicks the "Enroll authenticator" deep-link.
   *  Lets the parent reset any wrapping state (e.g. bannerVerifyOpen)
   *  so the dialog doesn't ride along behind /security after navigation. */
  onEnroll?: () => void;
}

export function MfaStepUpDialog({
  open,
  title = "Verify it's you",
  description = "Your account requires multi-factor authentication. Verify now to continue.",
  cancelLabel = "Cancel",
  onSuccess,
  onCancel,
  onEnroll,
}: Props) {
  return open ? (
    <MfaStepUpBody
      title={title}
      description={description}
      cancelLabel={cancelLabel}
      onSuccess={onSuccess}
      onCancel={onCancel}
      onEnroll={onEnroll}
    />
  ) : null;
}

function MfaStepUpBody({
  title,
  description,
  cancelLabel,
  onSuccess,
  onCancel,
  onEnroll,
}: {
  title: string;
  description: string;
  cancelLabel: string;
  onSuccess: () => void;
  onCancel: () => void;
  onEnroll?: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const isEntra = user?.app_metadata?.provider === "azure";

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [needsEnroll, setNeedsEnroll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: factors, error: fe } = await supabase.auth.mfa.listFactors();
      if (cancelled) return;
      if (fe) {
        setError(fe.message);
        return;
      }
      const factor = factors.totp.find((f) => f.status === "verified");
      if (!factor) {
        setNeedsEnroll(true);
        return;
      }
      const { data: chal, error: ce } = await supabase.auth.mfa.challenge({
        factorId: factor.id,
      });
      if (cancelled) return;
      if (ce) {
        setError(ce.message);
        return;
      }
      setFactorId(factor.id);
      setChallengeId(chal.id);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId || !challengeId) return;
    setBusy(true);
    setError(null);
    try {
      const { error: ve } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code,
      });
      if (ve) throw ve;
      // verify() updates the session; auth store's onAuthStateChange picks
      // up the new is_mfa_active=true claim and the parent re-renders.
      onSuccess();
    } catch (err) {
      setError(formatError(err, "Verification failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>

          {isEntra && (
            <Alert severity="info" icon={<MicrosoftIcon fontSize="small" />}>
              You signed in via Microsoft Entra. Manna requires its own authenticator code for
              privileged actions.
            </Alert>
          )}

          {needsEnroll ? (
            <>
              <Alert severity="warning">
                You haven't enrolled an authenticator app yet. Enrolling takes ~30 seconds — you'll
                only need to do this once.
              </Alert>
              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to={paths.security}
                onClick={() => onEnroll?.()}
              >
                Enroll authenticator
              </Button>
            </>
          ) : (
            <form onSubmit={handleTotpSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Authentication code"
                  value={code}
                  onChange={(ev) => setCode(ev.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  autoFocus
                  fullWidth
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={code.length !== 6 || busy || !challengeId}
                >
                  {busy ? "Verifying…" : "Verify"}
                </Button>
                <Typography variant="caption" color="text.secondary" align="center">
                  Lost your authenticator?{" "}
                  <Link component={RouterLink} to={paths.security}>
                    Manage factors
                  </Link>
                </Typography>
              </Stack>
            </form>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
